import { FunctionComponent, MouseEventHandler } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { isABTestActive, isSmartomat } from '@/utils/params';
import { useCanPrintOnView } from '@/hooks/useCanPrintOnView';
import { addTextConfigurationAction } from '@/redux/configurations/actions/addTextConfigurationAction';
import { getFeatures } from '@/redux/core/getters/getFeatures';
import { StoreDispatch } from '@/redux/types';
import { Design } from '@/components/common/icons/Design';
import { Shirt } from '@/components/common/icons/Shirt';
import { Text } from '@/components/common/icons/Text';
import { Upload } from '@/components/common/icons/Upload';
import { Modal, ModalContent, ModalTypes } from '@/components/common/modal/modal';
import './ZeroStatePanel.scss';

export const ZeroStatePanel: FunctionComponent = () => {
  const dispatch = useDispatch<StoreDispatch>();
  const navigate = useNavigate();
  const isActive = Boolean(useMatch('/get-started'));
  const isExpandedViewAbTest = isABTestActive('2024-19_B_Expanded');

  const { showProductTypeGallery, showDesignGallery, showAddText, showDesignUpload } =
    useSelector(getFeatures);

  const [, canPrintTextOnView, , canPrintTextOnProduct] = useCanPrintOnView();

  if (!isActive) {
    return null;
  }

  const closePanel = () => navigate('/');

  const handleClose = () => {
    trackFeature('zero-state-panel', { action: 'dismiss' });
    closePanel();
  };

  const handleProductClick: MouseEventHandler = () => {
    trackFeature('zero-state-panel', { action: 'product' });
  };

  const handleDesignClick: MouseEventHandler = () => {
    trackFeature('zero-state-panel', { action: 'design' });
  };

  const handleTextClick: MouseEventHandler = () => {
    trackFeature('zero-state-panel', { action: 'text' });
    if (isSmartomat()) {
      navigate('/text');
    } else {
      dispatch(addTextConfigurationAction(t('general.defaultText')));
      closePanel();
    }
  };

  const handleUploadClick: MouseEventHandler = () => {
    trackFeature('zero-state-panel', { action: 'upload' });
  };

  const title = t('zeroStatePanel.headline');

  const createABTestClassname = () => {
    if (isExpandedViewAbTest) {
      let className = 'zspAB';
      if (isSmartomat()) className += ' zspAB--fadeIn';
      return className;
    } else {
      return 'zsp';
    }
  };

  return (
    <Modal
      title={title}
      className={createABTestClassname()}
      type={isExpandedViewAbTest ? ModalTypes.zsp : ModalTypes.dialog}
      onClose={handleClose}
    >
      <ModalContent>
        <h3 className="zsp__headline">{title}</h3>
        <div className="zsp__tiles">
          {showProductTypeGallery && (
            <Link to="/productTypes" className="zsp-tile" onClick={handleProductClick}>
              <Shirt className={isExpandedViewAbTest ? 'zsp-tile__iconAB' : 'zsp-tile__icon'} />
              <div className="zsp-tile__text">{t('zeroStatePanel.product')}</div>
            </Link>
          )}
          {showDesignGallery && (
            <Link to="/designs" className="zsp-tile" onClick={handleDesignClick}>
              <Design className={isExpandedViewAbTest ? 'zsp-tile__iconAB' : 'zsp-tile__icon'} />
              <div className="zsp-tile__text">{t('zeroStatePanel.design')}</div>
            </Link>
          )}
          {showAddText && canPrintTextOnProduct && (
            <button
              className="zsp-tile"
              onClick={handleTextClick}
              type="button"
              disabled={!canPrintTextOnView}
            >
              <Text className={isExpandedViewAbTest ? 'zsp-tile__iconAB' : 'zsp-tile__icon'} />
              <div className="zsp-tile__text">{t('zeroStatePanel.text')}</div>
            </button>
          )}
          {showDesignUpload && (
            <Link to="/upload" className="zsp-tile" onClick={handleUploadClick}>
              <Upload className={isExpandedViewAbTest ? 'zsp-tile__iconAB' : 'zsp-tile__icon'} />
              <div className="zsp-tile__text">{t('zeroStatePanel.upload')}</div>
            </Link>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
